import React, { useEffect, useRef } from "react"
import { useAppflow } from "app/hooks/useAppFlow"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { SubmitHandler, useForm } from "react-hook-form"
import {
  getFormData,
  getSimulateProposalsResponseStatus,
  getValidatedStep
} from "state/form/selectors"
import { setData, setValidatedStep, trackLeadThunk } from "state/form/slice"
import { FormStateData } from "state/form/types"
import Header from "app/components/Form/Header"
import Form from "app/components/Form"
import Footer from "app/components/Form/Footer"
import { getTrackLeadPayload } from "../trackLeadPayload"
import { ProductComponent } from "../types"
import { loaSteps } from "./trackLeadPayload"
import { Success } from "./steps/success"
import Step1 from "./steps/Step1"
import Step2 from "./steps/Step2"
import Step3 from "./steps/Step3"
import Step4 from "./steps/Step4"

enum FormSteps {
  PERSONAL_INFO = 1,
  CAR_MODEL = 2,
  PAYMENT_PREFERENCE = 3,
  KBC_WIDGET = 4
}

export const Loa: React.FC<ProductComponent> = ({ currentStep }) => {
  const { navigate, clientType, carType, product, step, path } = useAppflow()

  const formData = useSelector(getFormData)
  const validatedStep = useSelector(getValidatedStep)
  const { isPending } = useSelector(getSimulateProposalsResponseStatus)
  const nativeNavigate = useNavigate()
  const dispatch = useDispatch()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const prevStepRef = useRef<number | undefined>()
  const { current: prevStep } = prevStepRef

  const { control, getValues } = useForm<FormStateData>({
    defaultValues: formData
  })

  const STEPS_COMPONENTS: {
    [key: number]: React.FC<any>
  } = {
    [FormSteps.PERSONAL_INFO]: Step1,
    [FormSteps.CAR_MODEL]: Step2,
    [FormSteps.PAYMENT_PREFERENCE]: Step3,
    [FormSteps.KBC_WIDGET]: Step4
  }

  const numberOfSteps = Object.keys(STEPS_COMPONENTS).length

  useEffect(() => {
    if (!step) return
    const activeStep = Math.min(validatedStep + 1, numberOfSteps)

    if (currentStep > validatedStep + 1 || currentStep > numberOfSteps) {
      navigate(clientType!, carType, product, activeStep)
    } else if (currentStep === 0 || isNaN(currentStep)) {
      navigate(clientType!, carType, product, 1)
    }
  }, [
    step,
    validatedStep,
    currentStep,
    navigate,
    clientType,
    carType,
    product,
    numberOfSteps
  ])

  useEffect(() => {
    prevStepRef.current = currentStep
    if (!prevStep) return

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    })
  }, [currentStep])

  const handleFormSubmit: SubmitHandler<FormStateData> = async payload => {
    dispatch(setData(payload))
    dispatch(setValidatedStep(currentStep))

    switch (currentStep) {
      case FormSteps.PERSONAL_INFO:
        dispatch(
          trackLeadThunk(
            getTrackLeadPayload({
              step: currentStep,
              payload,
              config: loaSteps,
              product: product!,
              carType: carType!,
              path,
              leadRecordType: clientType
            })
          )
        )
        break
      case FormSteps.CAR_MODEL:
        dispatch(
          trackLeadThunk(
            getTrackLeadPayload({
              step: currentStep,
              payload,
              config: loaSteps,
              product: product!,
              carType: carType!,
              path,
              leadRecordType: clientType
            })
          )
        )
        break
      case FormSteps.PAYMENT_PREFERENCE:
        dispatch(
          trackLeadThunk(
            getTrackLeadPayload({
              step: currentStep,
              payload,
              config: loaSteps,
              product: product!,
              carType: carType!,
              path,
              leadRecordType: clientType,
              journeyCompleted: true // Nodig omdat de knop "aanvraag voltooien" hier is verwijderd.
            })
          )
        )
        break
      case FormSteps.KBC_WIDGET:
        dispatch(
          trackLeadThunk(
            getTrackLeadPayload({
              step: currentStep,
              payload,
              config: loaSteps,
              product: product!,
              carType: carType!,
              path,
              leadRecordType: clientType,
              journeyCompleted: true
            })
          )
        )
        break
    }

    if (currentStep === numberOfSteps) {
      nativeNavigate("/success")
    } else {
      navigate(clientType!, carType, product, currentStep + 1)
    }
  }

  const handlePrevStep = () => {
    // needed because "complete" button is removed in this flow.
    const isLastStep = currentStep === numberOfSteps
    navigate(clientType!, carType, product, isLastStep ? 1 : currentStep - 1)
  }

  const Component: React.FC<any> | undefined = STEPS_COMPONENTS[currentStep]

  if (!Component) {
    return <Success />
  }

  return (
    <>
      <Header ref={scrollRef} numberOfSteps={numberOfSteps} />

      <Form
        defaultValues={formData}
        onValid={handleFormSubmit}
        isPending={isPending}
      >
        {Component && <Component control={control} getValues={getValues} />}
        <Footer onPrev={handlePrevStep} numberOfSteps={numberOfSteps} />
      </Form>
    </>
  )
}
